'use client';

import React, { useMemo, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import { Exchange, Segment } from '@/types/symbols/exchangeTypes';
import { useExchangeWebSocket } from '@/hooks/symbol/useExchangeWebSocket';
import { getPrice } from '@/utils/symbols/exchangeUtils';
import SegmentTabs from '@/components/(postTypes)/symbols/single/coin/exchange-live/SegmentTabs';
import ArbitrageProfitDisplay from '@/components/(postTypes)/symbols/single/coin/exchange-live/ArbitrageProfitDisplay';
import ExchangeRow from '@/components/(postTypes)/symbols/single/coin/exchange-live/ExchangeRow';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface SingleCoinExchangesListProps {
    name_fa: string;
    symbol: string;
    type: number;
    initialExchangeList?: Exchange[];
}

const SingleCoinExchangesListLive: React.FC<SingleCoinExchangesListProps> = ({
    symbol,
    initialExchangeList = [],
}) => {
    const [segment, setSegment] = useState<Segment>('buy');

    const { exchanges, priceFlashes, arbitrageProfit, isUSDT, profitDirection } =
        useExchangeWebSocket(symbol, initialExchangeList, segment);

    const sorted = useMemo(() => {
        const arr = [...exchanges];
        arr.sort((a, b) => {
            const ap = getPrice(a, segment);
            const bp = getPrice(b, segment);
            return segment === 'buy' ? ap - bp : bp - ap;
        });
        return arr;
    }, [exchanges, segment]);

    const bestPrice = useMemo(() => {
        if (!sorted.length) return null;
        const prices = sorted.map((ex) => getPrice(ex, segment)).filter((p) => p > 0);
        if (!prices.length) return null;
        return segment === 'buy' ? Math.min(...prices) : Math.max(...prices);
    }, [sorted, segment]);

    return (
        <div className='w-full'>
            <SegmentTabs segment={segment} onSegmentChange={setSegment} />

            {isUSDT && (
                <ArbitrageProfitDisplay profit={arbitrageProfit} direction={profitDirection} />
            )}

            <div className='mt-3 border rounded-xl overflow-x-auto bg-white/80 dark:bg-primary-dark/60 backdrop-blur-xs border-gray-200 dark:border-white/10 shadow-xs scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-white/10'>
                <Table className='min-w-full overflow-hidden'>
                    <TableHeader>
                        <TableRow className='text-xs bg-slate-100/70 dark:bg-white/5 border-b border-gray-100 dark:border-white/10'>
                            <TableHead className='w-[36px] min-w-[36px] text-center font-bold whitespace-nowrap px-2'>
                                #
                            </TableHead>
                            <TableHead className='text-right font-bold whitespace-nowrap min-w-[140px]'>
                                نام صرافی
                            </TableHead>
                            <TableHead className='text-right font-bold whitespace-nowrap min-w-[120px]'>
                                قیمت (تومان)
                            </TableHead>
                            <TableHead className='text-right font-bold whitespace-nowrap min-w-[100px]'>
                                حجم معامله
                            </TableHead>
                            <TableHead className='text-center font-bold whitespace-nowrap w-[90px] min-w-[90px]'>
                                {segment === 'buy' ? 'اقدام به خرید' : 'اقدام به فروش'}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <AnimatePresence initial={false}>
                            {sorted.map((ex, idx) => (
                                <ExchangeRow
                                    key={ex.slug}
                                    exchange={ex}
                                    index={idx}
                                    segment={segment}
                                    isBestPrice={
                                        bestPrice != null && getPrice(ex, segment) === bestPrice
                                    }
                                    flash={priceFlashes[ex.slug]}
                                />
                            ))}
                        </AnimatePresence>
                    </TableBody>
                </Table>
            </div>
        </div>
    );
};

export default React.memo(SingleCoinExchangesListLive);
